import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Sparkles,
  ArrowLeft,
  Loader2,
  Users,
  MousePointer,
  Eye,
  ShoppingCart,
  Mail,
  TrendingUp,
  RefreshCw,
  Download,
  LogOut,
  Lock,
  Send,
} from "lucide-react";
import {
  adminLogin,
  adminLogout,
  getAnalytics,
  getLeads,
  exportLeadsCSV,
  getMockedEmails,
  getTopicHeatmap,
} from "@/lib/api";
import { set } from "date-fns";

export default function AdminPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [leads, setLeads] = useState([]);
  const [mockedEmails, setMockedEmails] = useState([]);
  const [activeTab, setActiveTab] = useState("leads"); // 'leads', 'emails', 'heatmap'
  const [heatmap, setHeatmap] = useState(null);

  // Login form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    checkAuthAndLoadData();
  }, []);

  const checkAuthAndLoadData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, leadsRes, emailsRes] = await Promise.all([
        getAnalytics(),
        getLeads(),
        getMockedEmails(),
      ]);
      setAnalytics(analyticsRes.data);
      setLeads(leadsRes.data.leads || []);
      setMockedEmails(emailsRes.data.emails || []);
      setIsAuthenticated(true);
      getTopicHeatmap()
        .then((res) => setHeatmap(res.data))
        .catch(() => {});
    } catch (error) {
      // Not authenticated
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);

    try {
      await adminLogin(email, password);

      setIsAuthenticated(true);
      toast.success("Sikeres bejelentkezés!");
      try {
        await loadData();
      } catch (err) {
        console.warn("Első adatlekérés sikertelen, újrapróbálás...");
        setTimeout(() => loadData(), 1000);
      }
    } catch (error) {
      toast.error("Hibás email vagy jelszó");
      setIsAuthenticated(false);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminLogout();
      setIsAuthenticated(false);
      setAnalytics(null);
      setLeads([]);
      toast.success("Kijelentkezve");
    } catch (error) {
      console.error("Logout error:", error);
      setIsAuthenticated(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await exportLeadsCSV();
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "kardiy_leads.csv";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("CSV letöltve!");
    } catch (error) {
      toast.error("Hiba a CSV letöltése során");
      console.error(error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, leadsRes, emailsRes] = await Promise.all([
        getAnalytics(),
        getLeads(),
        getMockedEmails(),
      ]);
      setAnalytics(analyticsRes.data);
      setLeads(leadsRes.data.leads || []);
      setMockedEmails(emailsRes.data.emails || []);
      getTopicHeatmap()
        .then((res) => setHeatmap(res.data))
        .catch(() => {});
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Login screen
  if (!isAuthenticated && !loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FDFBF7] to-[#FFF1F2] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white rounded-3xl shadow-lg border-0">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-[#FF6B6B]/10 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-[#FF6B6B]" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-[#292524]">
                Admin Bejelentkezés
              </h1>
              <p className="text-stone-500 mt-2">
                Add meg az admin hitelesítő adatokat
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#292524]">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  className="rounded-xl border-stone-200"
                  required
                  data-testid="admin-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#292524]">
                  Jelszó
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="rounded-xl border-stone-200"
                  required
                  data-testid="admin-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loginLoading}
                className="w-full bg-[#FF6B6B] hover:bg-[#FF5252] text-white font-bold py-6 rounded-full"
                data-testid="admin-login-btn"
              >
                {loginLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Bejelentkezés...
                  </>
                ) : (
                  "Bejelentkezés"
                )}
              </Button>
            </form>

            <button
              onClick={() => navigate("/")}
              className="w-full mt-4 text-stone-500 hover:text-[#292524] text-sm"
            >
              Vissza a főoldalra
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF6B6B]" />
      </div>
    );
  }

  const funnel = analytics?.funnel || {};
  const rates = analytics?.conversion_rates || {};

  return (
    <>
      <Helmet>
        <title>Admin | Kardiy</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    <div className="min-h-screen bg-gradient-to-b from-[#FDFBF7] to-[#FFF1F2]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-stone-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-stone-500 hover:text-[#292524] transition-colors p-2 -ml-2"
              data-testid="admin-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#FF6B6B]" />
              <span className="font-heading text-lg font-bold text-[#292524]">
                Admin
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={loadData}
                className="p-2 text-stone-500 hover:text-[#292524]"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 -mr-2 text-stone-500 hover:text-[#FF6B6B]"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-8 max-w-4xl mx-auto">
        {/* Funnel Stats */}
        <h2 className="font-heading text-2xl font-bold text-[#292524] mb-6">
          Statisztikák
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-white rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <Eye className="w-6 h-6 text-[#FF6B6B] mx-auto mb-2" />
              <p className="text-3xl font-bold text-[#292524]">
                {funnel.landing_page_views || 0}
              </p>
              <p className="text-xs text-stone-500">Főoldal megtekintés</p>
            </CardContent>
          </Card>

          <Card className="bg-white rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <MousePointer className="w-6 h-6 text-[#FCD34D] mx-auto mb-2" />
              <p className="text-3xl font-bold text-[#292524]">
                {funnel.create_page_views || 0}
              </p>
              <p className="text-xs text-stone-500">Készítés indítva</p>
              <p className="text-xs text-[#84A98C] mt-1">
                {rates.landing_to_create}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-6 h-6 text-[#84A98C] mx-auto mb-2" />
              <p className="text-3xl font-bold text-[#292524]">
                {funnel.editor_page_views || 0}
              </p>
              <p className="text-xs text-stone-500">Szerkesztő elérve</p>
              <p className="text-xs text-[#84A98C] mt-1">
                {rates.create_to_editor}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <ShoppingCart className="w-6 h-6 text-[#FCD34D] mx-auto mb-2" />
              <p className="text-3xl font-bold text-[#292524]">
                {funnel.cart_adds || 0}
              </p>
              <p className="text-xs text-stone-500">Kosárba helyezés</p>
              <p className="text-xs text-[#84A98C] mt-1">
                {rates.editor_to_cart}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <ShoppingCart className="w-6 h-6 text-[#FF6B6B] mx-auto mb-2" />
              <p className="text-3xl font-bold text-[#292524]">
                {funnel.buy_button_clicks || 0}
              </p>
              <p className="text-xs text-stone-500">Vásárlás kattintás</p>
              <p className="text-xs text-[#84A98C] mt-1">
                {rates.editor_to_buy_click}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <Mail className="w-6 h-6 text-[#8B5CF6] mx-auto mb-2" />
              <p className="text-3xl font-bold text-[#292524]">
                {funnel.waitlist_signups || 0}
              </p>
              <p className="text-xs text-stone-500">Feliratkozás</p>
              <p className="text-xs text-[#84A98C] mt-1">
                {rates.buy_click_to_signup}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("leads")}
            className={`px-4 py-2 rounded-full font-medium transition-all ${
              activeTab === "leads"
                ? "bg-[#FF6B6B] text-white"
                : "bg-white text-stone-500 hover:bg-stone-50"
            }`}
            data-testid="tab-leads"
          >
            <Users className="w-4 h-4 inline mr-2" />
            Feliratkozók ({leads.length})
          </button>
          <button
            onClick={() => setActiveTab("emails")}
            className={`px-4 py-2 rounded-full font-medium transition-all ${
              activeTab === "emails"
                ? "bg-[#FF6B6B] text-white"
                : "bg-white text-stone-500 hover:bg-stone-50"
            }`}
            data-testid="tab-emails"
          >
            <Send className="w-4 h-4 inline mr-2" />
            Emailek ({mockedEmails.length})
            <span className="ml-1 text-xs opacity-70">(MOCKED)</span>
          </button>
          <button
            onClick={() => setActiveTab("heatmap")}
            className={`px-4 py-2 rounded-full font-medium transition-all ${
              activeTab === "heatmap"
                ? "bg-[#FF6B6B] text-white"
                : "bg-white text-stone-500 hover:bg-stone-50"
            }`}
            data-testid="tab-heatmap"
          >
            <TrendingUp className="w-4 h-4 inline mr-2" />
            Témák hőtérképe
          </button>
        </div>

        {/* Leads Tab */}
        {activeTab === "leads" && (
          <>
            {/* Leads List Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold text-[#292524]">
                Feliratkozók ({leads.length})
              </h2>
              {leads.length > 0 && (
                <Button
                  onClick={handleExportCSV}
                  variant="outline"
                  className="rounded-full border-[#FF6B6B] text-[#FF6B6B] hover:bg-[#FF6B6B]/10"
                  data-testid="export-csv-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  CSV letöltés
                </Button>
              )}
            </div>

            {leads.length === 0 ? (
              <Card className="bg-white rounded-2xl border-0 shadow-sm">
                <CardContent className="p-8 text-center">
                  <Users className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500">Még nincsenek feliratkozók</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {leads.map((lead, i) => (
                  <Card
                    key={lead.id || i}
                    className="bg-white rounded-2xl border-0 shadow-sm overflow-hidden"
                    data-testid={`lead-card-${i}`}
                  >
                    <CardContent className="p-0">
                      {/* Header with email and date */}
                      <div className="bg-stone-50 px-4 py-3 border-b border-stone-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#FF6B6B]/10 flex items-center justify-center">
                              <Mail className="w-5 h-5 text-[#FF6B6B]" />
                            </div>
                            <div>
                              <p className="font-medium text-[#292524]">
                                {lead.email}
                              </p>
                              <p className="text-xs text-stone-500">
                                {lead.last_name} {lead.first_name}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-[#FF6B6B]">
                              {lead.total_price?.toLocaleString()} Ft
                            </p>
                            <p className="text-xs text-stone-400">
                              {new Date(lead.created_at).toLocaleDateString(
                                "hu-HU",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Generated Cards / Project IDs Section */}
                      <div className="px-4 py-3">
                        {lead.project_ids && lead.project_ids.length > 0 ? (
                          <div className="space-y-3">
                            <p className="text-xs font-medium text-stone-500 uppercase tracking-wide">
                              Generált paklik ({lead.project_ids.length})
                            </p>
                            {lead.project_ids.map((projectId, pi) => {
                              const chain = lead.selection_chains?.[pi];
                              return (
                                <div
                                  key={projectId}
                                  className="bg-stone-50 rounded-xl p-3"
                                  data-testid={`project-${projectId}`}
                                >
                                  <div className="flex items-start gap-2">
                                    <div className="w-6 h-6 rounded-full bg-[#FCD34D]/20 flex items-center justify-center flex-shrink-0">
                                      <span className="text-xs font-bold text-[#292524]">
                                        {pi + 1}
                                      </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-mono text-stone-400 truncate mb-1">
                                        ID: {projectId}
                                      </p>
                                      {chain && (
                                        <div className="flex flex-wrap gap-1">
                                          <span className="text-xs px-2 py-0.5 bg-[#FF6B6B]/10 text-[#FF6B6B] rounded-full">
                                            {chain.category}
                                          </span>
                                          <span className="text-xs text-stone-300">
                                            →
                                          </span>
                                          <span className="text-xs px-2 py-0.5 bg-[#FCD34D]/20 text-[#292524] rounded-full">
                                            {chain.level1}
                                          </span>
                                          <span className="text-xs text-stone-300">
                                            →
                                          </span>
                                          <span className="text-xs px-2 py-0.5 bg-[#84A98C]/20 text-[#292524] rounded-full">
                                            {chain.level2}
                                          </span>
                                          <span className="text-xs text-stone-300">
                                            →
                                          </span>
                                          <span className="text-xs px-2 py-0.5 bg-[#8B5CF6]/10 text-[#8B5CF6] rounded-full">
                                            {chain.level3}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-xs text-stone-400 italic">
                            Nincs generált pakli
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Emails Tab */}
        {activeTab === "emails" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold text-[#292524]">
                Küldött emailek ({mockedEmails.length})
              </h2>
              <span className="px-3 py-1 bg-[#FCD34D]/20 text-[#292524] rounded-full text-xs font-medium">
                MOCKED - Valódi küldéshez SendGrid API kulcs szükséges
              </span>
            </div>

            {mockedEmails.length === 0 ? (
              <Card className="bg-white rounded-2xl border-0 shadow-sm">
                <CardContent className="p-8 text-center">
                  <Send className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500">
                    Még nincsenek küldött emailek
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {mockedEmails.map((emailItem, i) => (
                  <Card
                    key={i}
                    className="bg-white rounded-2xl border-0 shadow-sm"
                    data-testid={`email-card-${i}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#8B5CF6]/10 flex items-center justify-center flex-shrink-0">
                          <Send className="w-5 h-5 text-[#8B5CF6]" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-medium text-[#292524]">
                                {emailItem.to}
                              </p>
                              <p className="text-xs text-stone-500">
                                Címzett: {emailItem.first_name}
                              </p>
                            </div>
                            <div className="text-right">
                              <span className="px-2 py-1 bg-[#84A98C]/10 text-[#84A98C] rounded-full text-xs font-medium">
                                {emailItem.status}
                              </span>
                              <p className="text-xs text-stone-400 mt-1">
                                {new Date(emailItem.sent_at).toLocaleDateString(
                                  "hu-HU",
                                  {
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )}
                              </p>
                            </div>
                          </div>
                          <p className="text-sm font-medium text-[#292524] mb-1">
                            {emailItem.subject}
                          </p>
                          <div className="bg-stone-50 rounded-lg p-3 text-xs text-stone-600 whitespace-pre-wrap max-h-32 overflow-y-auto">
                            {emailItem.body}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Heatmap Tab */}
        {activeTab === "heatmap" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold text-[#292524]">
                Témák hőtérképe
              </h2>
              <span className="text-xs text-stone-400">
                {heatmap?.total_projects || 0} generálás összesen
              </span>
            </div>

            {!heatmap || heatmap.categories.length === 0 ? (
              <Card className="bg-white rounded-2xl border-0 shadow-sm">
                <CardContent className="p-8 text-center">
                  <TrendingUp className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500">Még nincs generálási adat</p>
                </CardContent>
              </Card>
            ) : (
              (() => {
                const maxCount = Math.max(
                  1,
                  ...heatmap.categories.flatMap((cat) =>
                    cat.level1.flatMap((l1) => l1.level2.map((l2) => l2.count))
                  )
                );
                return (
                  <div className="space-y-6">
                    {heatmap.categories.map((cat) => (
                      <Card
                        key={cat.name}
                        className="bg-white rounded-2xl border-0 shadow-sm overflow-hidden"
                      >
                        <div className="bg-[#FF6B6B]/10 px-5 py-3 flex items-center justify-between">
                          <span className="font-heading font-bold text-[#292524]">
                            {cat.name}
                          </span>
                          <span className="text-sm font-medium text-[#FF6B6B]">
                            {cat.total} generálás
                          </span>
                        </div>
                        <CardContent className="p-4 space-y-4">
                          {cat.level1.map((l1) => (
                            <div key={l1.name}>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
                                  {l1.name}
                                </span>
                                <span className="text-xs text-stone-400">
                                  ({l1.total})
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {l1.level2.map((l2) => {
                                  const intensity = l2.count / maxCount;
                                  const opacity = Math.round(10 + intensity * 90);
                                  return (
                                    <div
                                      key={l2.name}
                                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl"
                                      style={{
                                        backgroundColor: `rgba(255, 107, 107, ${opacity / 100})`,
                                      }}
                                      title={`${l2.name}: ${l2.count} generálás`}
                                    >
                                      <span
                                        className="text-xs font-medium"
                                        style={{
                                          color: intensity > 0.5 ? "#fff" : "#292524",
                                        }}
                                      >
                                        {l2.name}
                                      </span>
                                      <span
                                        className="text-xs font-bold"
                                        style={{
                                          color: intensity > 0.5 ? "rgba(255,255,255,0.8)" : "#FF6B6B",
                                        }}
                                      >
                                        {l2.count}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                );
              })()
            )}
          </>
        )}
      </main>
    </div>
    </>
  );
}
